//#region header */
/**************************************************************************************************
//
//  Description: Navigation Bar contents
//
//  Copyright:    © 2025 - 2026 Idox Software Limited
//
//--------------------------------------------------------------------------------------------------
//
//  Modification History:
//
//  Version Date     Modifier             Issue# Description
//#region Version 1.0.0.0 changes
//    001   21.02.25 Sean Flook          GMSCM-1 Initial Revision.
//    002   12.03.25 Sean Flook          GMSCM-1 Code required for managing the configuration and cluster documents.
//    003   13.03.25 Sean Flook          GMSCM-1 Added code to handle when a users token has expired.
//    004   05.02.26 Sean Flook         GMSCM-12 Added Application and Organisation functionality.
//    005   16.02.26 Joshua McCormick   GMSCM-12 Reworked and fixed side navigation not updating when switching pages after selecting a document.
//#endregion Version 1.0.0.0 changes
//
//--------------------------------------------------------------------------------------------------
//#endregion header */

import React, { useCallback, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import useConfigQueryStore from "../context/ConfigStore";
import useClusterQueryStore from "../context/ClusterStore";
import useApplicationQueryStore from "../context/ApplicationStore";
import useOrganisationQueryStore from "../context/OrganisationStore";

import useAddCluster from "../hooks/useAddCluster";
import useAddConfig from "../hooks/useAddConfig";
import useUpdateCluster from "../hooks/useUpdateCluster";
import useUpdateConfig from "../hooks/useUpdateConfig";
import usePostApplicationNames from "../hooks/usePostApplicationNames";
import usePostOrganisations from "../hooks/usePostOrganisations";

import { ApplicationsRoute, ClustersRoute, DocumentsRoute, OrganisationsRoute } from "../PageRouting";

import MultiTenantClusterGetDto from "../dtos/multiTenantClusterDto";
import MultiTenantConfigGetDto from "../dtos/multiTenantConfigDto";
import ClusterErrorType from "../models/clusterErrorType";
import ConfigErrorType from "../models/configErrorType";

import { validateCluster } from "../utils/ClusterValidation";
import { validateConfig } from "../utils/ConfigurationValidation";

import { Box, Drawer, Grid2, IconButton, Stack, Tooltip } from "@mui/material";
import ADSUserAvatar from "./ADSUserAvatar";

import ConfirmEditLossDialog from "../dialogs/ConfirmEditLossDialog";

import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import GroupsIcon from "@mui/icons-material/Groups";
import AppsIcon from "@mui/icons-material/Apps";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";

import { adsBlueA, adsLightGreyA50, adsLightGreyB, adsMidGreyA, adsWhite } from "../utils/ADSColour";
import { dataFormStyle, tooltipStyle } from "../utils/ADSStyles";
import useUserQueryStore from "../context/UserStore";
import useApplicationNames from "../hooks/useApplicationNames";
import useOrganisations from "../hooks/useOrganisations";
import { ApplicationNameDto } from "../dtos/applicationNamesDto";
import { ApplicationErrorType } from "../models/applicationErrorType";
import { validateApplicationData } from "../utils/ApplicationValidation";
import { OrganisationErrorType } from "../models/organisationErrorType";
import { validateOrganisationData } from "../utils/OrganisationValidation";

interface ADSNavContentProps {}

const ADSNavContent: React.FC<ADSNavContentProps> = () => {
  const navBarWidth: string = "60px";

  const location = useLocation();
  const currentPath = location.pathname;
  
  const isDocumentsActive = currentPath === DocumentsRoute || currentPath === "/";
  const isClustersActive = currentPath === ClustersRoute;
  const isApplicationsActive = currentPath === ApplicationsRoute;
  const isOrganisationsActive = currentPath === OrganisationsRoute;

  const setDisplayDialog = useUserQueryStore((s) => s.setDisplayDialog);

  const documentChanged = useConfigQueryStore((s) => s.configQuery.documentChanged);
  const configItem = useConfigQueryStore((s) => s.configQuery.configItem);
  const configurationDocuments = useConfigQueryStore((s) => s.configQuery.configurationDocuments);
  const newConfig = useConfigQueryStore((s) => s.configQuery.newConfig);
  const setNewConfig = useConfigQueryStore((s) => s.setNewConfig);
  const setDocumentChanged = useConfigQueryStore((s) => s.setDocumentChanged);
  const setDisableConfigSave = useConfigQueryStore((s) => s.setDisableSave);
  const setConfigValidationErrors = useConfigQueryStore((s) => s.setValidationErrors);
  const setRevertConfigChanges = useConfigQueryStore((s) => s.setRevertChanges);

  const clusterChanged = useClusterQueryStore((s) => s.clusterQuery.changed);
  const clusterItem = useClusterQueryStore((s) => s.clusterQuery.clusterItem);
  const clusters = useClusterQueryStore((s) => s.clusterQuery.clusters);
  const newCluster = useClusterQueryStore((s) => s.clusterQuery.newCluster);
  const setNewCluster = useClusterQueryStore((s) => s.setNewCluster);
  const setClusterChanged = useClusterQueryStore((s) => s.setChanged);
  const setDisableClusterSave = useClusterQueryStore((s) => s.setDisableSave);
  const setClusterValidationErrors = useClusterQueryStore((s) => s.setValidationErrors);
  const setRevertClusterChanges = useClusterQueryStore((s) => s.setRevertChanges);

  const applicationChanged = useApplicationQueryStore((s) => s.applicationQuery.applicationChanged);
  const applicationNames = useApplicationQueryStore((s) => s.applicationQuery.applicationNames);
  const setApplicationChanged = useApplicationQueryStore((s) => s.setApplicationChanged);
  const setApplicationValidationErrors = useApplicationQueryStore((s) => s.setValidationErrors);

  const organisationChanged = useOrganisationQueryStore((s) => s.organisationQuery.organisationChanged);
  const organisationNames = useOrganisationQueryStore((s) => s.organisationQuery.organisationNames);
  const setOrganisationChanged = useOrganisationQueryStore((s) => s.setOrganisationChanged);
  const setOrganisationValidationErrors = useOrganisationQueryStore((s) => s.setValidationErrors);


  const addConfig = useAddConfig((newData: MultiTenantConfigGetDto[]) => {
    setNewConfig(false);
    setDocumentChanged(false);
    setDisableConfigSave(true);
    if (currentType.current === "configuration" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_configuration") {
      setDisplayDialog(true);
    }
  });

  const updateConfig = useUpdateConfig((updatedData: MultiTenantConfigGetDto[]) => {
    setDocumentChanged(false);
    setDisableConfigSave(true);
    if (currentType.current === "configuration" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_configuration") {
      setDisplayDialog(true);
    }
  });

  const addCluster = useAddCluster((newData: MultiTenantClusterGetDto[]) => {
    setNewCluster(false);
    setClusterChanged(false);
    setDisableClusterSave(true);
    if (currentType.current === "cluster" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_cluster") {
      setDisplayDialog(true);
    }
  });

  const updateCluster = useUpdateCluster((updatedData: MultiTenantClusterGetDto[]) => {
    setClusterChanged(false);
    setDisableClusterSave(true);
    if (currentType.current === "cluster" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_cluster") {
      setDisplayDialog(true);
    }
  });

  const postApplicationNames = usePostApplicationNames((newData: ApplicationNameDto[]) => {
    setApplicationChanged(false);
    if (currentType.current === "application" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_application") {
      setDisplayDialog(true);
    }
  });

  const postOrganisations = usePostOrganisations((newData: string[]) => {
    setOrganisationChanged(false);
    if (currentType.current === "organisation" && pendingRoute.current) {
      window.location.href = pendingRoute.current;
    } else if (currentType.current === "user_organisation") {
      setDisplayDialog(true);
    }
  });

  const { data: applicationNamesData } = useApplicationNames();
  const { data: organisationsData } = useOrganisations();

  const [openConformation, setOpenConformation] = useState<boolean>(false);

  const currentType = useRef<string | null>(null);
  const pendingRoute = useRef<string | null>(null);

  const getUnsavedChangesType = useCallback((): string | null => {
    if (documentChanged) return "configuration";
    if (clusterChanged) return "cluster";
    if (applicationChanged) return "application";
    if (organisationChanged) return "organisation";
    return null;
  }, [documentChanged, clusterChanged, applicationChanged, organisationChanged]);

  // prevent default if there are unsaved changes
  const handleNavigationClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>, route: string) => {
    const unsavedChangesType = getUnsavedChangesType();
    if (unsavedChangesType) {
      event.preventDefault();
      pendingRoute.current = route;
      currentType.current = unsavedChangesType;
      setOpenConformation(true);
    }
  }, [getUnsavedChangesType]);

  /**
   * Method to handle the event when the user chooses to save the changes made to the current document.
   */
  const handleUserSaveChanges = (type: string): void => {
    currentType.current = type;
    setOpenConformation(true);
  };

  /**
   * Method to handle the event when the user chooses to save the changes made to the current document.
   */
  const handleSaveChanges = async (): Promise<void> => {
    setOpenConformation(false);

    if (["configuration", "user_configuration"].includes(currentType.current || "")) {
      if (configItem) {
        const nodeIds: string[] = configItem.split("|");
        const currentDocument: MultiTenantConfigGetDto | undefined = configurationDocuments?.find(
          (x) => x.id === nodeIds[0]
        );

        const validationErrors: ConfigErrorType[] | undefined = validateConfig(
          configurationDocuments!,
          applicationNamesData,
          organisationsData,
          currentDocument
        );

        if (!validationErrors || validationErrors.length === 0) {
          setConfigValidationErrors(undefined);
          if (currentDocument) {
            const cleanedDocument = {
              ...currentDocument,
              organisations: currentDocument.organisations.map((org) => ({
                ...org,
                namedConnectionSettings: (org.namedConnectionSettings ?? []).map((setting) => ({
                  ...setting,
                  settings: { ...setting.settings },
                })),
                namedElasticSettings: (org.namedElasticSettings ?? []).map((elastic) => ({
                  ...elastic,
                  settings: {
                    ...elastic.settings,
                    elasticNodes: (elastic.settings?.elasticNodes ?? []).filter((node) => node.trim() !== ""),
                  },
                })),
              })),
            };

            if (newConfig) {
              addConfig.mutate(cleanedDocument);
            } else {
              updateConfig.mutate(cleanedDocument);
            }
          }
        } else {
          setConfigValidationErrors(validationErrors);
        }
      }
    } else if (["cluster", "user_cluster"].includes(currentType.current || "")) {
      if (clusterItem) {
        const nodeIds: string[] = clusterItem.split("|");
        const currentCluster: MultiTenantClusterGetDto | undefined = clusters?.find((x) => x.id === nodeIds[0]);

        const validationErrors: ClusterErrorType[] | undefined = validateCluster(clusters!, currentCluster);

        if (!validationErrors || validationErrors.length === 0) {
          setClusterValidationErrors(undefined);
          if (currentCluster) {
            if (newCluster) {
              addCluster.mutate(currentCluster);
            } else {
              updateCluster.mutate(currentCluster);
            }
          }
        } else {
          setClusterValidationErrors(validationErrors);
        }
      }
    } else if (["application", "user_application"].includes(currentType.current || "")) {
      if (applicationNames) {
        const validationErrors: ApplicationErrorType[] | undefined = validateApplicationData(
          applicationNames.map((app, index) => ({ id: index, ...app }))
        );

        if (!validationErrors || validationErrors.length === 0) {
          setApplicationValidationErrors(undefined);
          postApplicationNames.mutate(applicationNames || []);
        } else {
          setApplicationValidationErrors(validationErrors);
        }
      }
    } else if (["organisation", "user_organisation"].includes(currentType.current || "")) {
      if (organisationNames) {
        const validationErrors: OrganisationErrorType[] | undefined = validateOrganisationData(
          organisationNames.map((org, index) => ({ id: index, organisation: org }))
        );
        if (!validationErrors || validationErrors.length === 0) {
          setOrganisationValidationErrors(undefined);
          postOrganisations.mutate(organisationNames || []);
        } else {
          setOrganisationValidationErrors(validationErrors);
        }
      }
    }
  };

  /**
   * Method to handle the event when the user chooses to dispose of any changes made to the current document.
   */
  const handleAllowDispose = (): void => {
    setOpenConformation(false);

    switch (currentType.current) {
      case "configuration":
        setDocumentChanged(false);
        setDisableConfigSave(true);
        if (pendingRoute.current) {
          window.location.href = pendingRoute.current;
        }
        break;

      case "user_configuration":
        setRevertConfigChanges(true);
        setDocumentChanged(false);
        setDisableConfigSave(true);
        setDisplayDialog(true);
        break;

      case "cluster":
        setClusterChanged(false);
        setDisableClusterSave(true);
        if (pendingRoute.current) {
          window.location.href = pendingRoute.current;
        }
        break;

      case "user_cluster":
        setRevertClusterChanges(true);
        setClusterChanged(false);
        setDisableClusterSave(true);
        setDisplayDialog(true);
        break;

      case "application":
        setApplicationChanged(false);
        if (pendingRoute.current) {
          window.location.href = pendingRoute.current;
        }
        break;

      case "organisation":
        setOrganisationChanged(false);
        if (pendingRoute.current) {
          window.location.href = pendingRoute.current;
        }
        break;

      default:
        break;
    }
  };

  /**
   * Method to handle the event when the user cancels moving away from the dialog.
   */
  const handleCancelMoveAway = (): void => {
    setOpenConformation(false);
  };

  /**
   * Method to get the navigation icon styling.
   *
   * @param {boolean} open True if the navigation group is open; otherwise false.
   * @returns {object} The styling for the navigation icon.
   */
  const navIconButtonStyle = {
    p: "8px",
    borderRadius: 0,
    borderLeft: "none",
    "&:hover": {
      backgroundColor: "transparent",
    },
    "&.Mui-focusVisible": {
      backgroundColor: "transparent",
      borderLeft: "none",
    },
  };

  function navigationIconStyle(open: boolean): object {
    if (open) return { color: adsBlueA };
    else return { color: adsMidGreyA };
  }

  return (
    <>
      <Drawer
        variant="permanent"
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          width: `${navBarWidth}px`,
          "& .MuiDrawer-paper": {
            width: `${navBarWidth}px`,
            backgroundColor: adsWhite,
            borderRight: `1px solid ${adsLightGreyB}`,
            borderLeft: "none",
            boxShadow: `4px 0px 9px ${adsLightGreyA50}`,
          },
        })}
      >
        <Grid2
          sx={dataFormStyle("ADSNavContent")}
          container
          direction="column"
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid2 sx={(theme) => ({ mt: theme.spacing(1.3) })} size="grow">
            <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
              <img src="/images/Idox_Logo.svg" alt="IDOX Group" width="36" />
              <Box sx={{ height: 20 }} />
              <Tooltip title="Configuration documents" arrow placement="right" sx={tooltipStyle}>
                <a href={DocumentsRoute} onClick={(e) => handleNavigationClick(e, DocumentsRoute)} style={{ textDecoration: 'none' }}>
                  <IconButton
                    aria-label="configuration-documents"
                    size="large"
                    component="span"
                    disableRipple
                    sx={navIconButtonStyle}
                  >
                    <DocumentScannerIcon fontSize="large" sx={navigationIconStyle(isDocumentsActive)} />
                  </IconButton>
                </a>
              </Tooltip>
              <Tooltip title="Clusters" arrow placement="right" sx={tooltipStyle}>
                <a href={ClustersRoute} onClick={(e) => handleNavigationClick(e, ClustersRoute)} style={{ textDecoration: 'none' }}>
                  <IconButton
                    aria-label="clusters"
                    size="large"
                    component="span"
                    disableRipple
                    sx={navIconButtonStyle}
                  >
                    <GroupsIcon fontSize="large" sx={navigationIconStyle(isClustersActive)} />
                  </IconButton>
                </a>
              </Tooltip>
            </Stack>
          </Grid2>
          <Grid2>
            <Stack direction="column" spacing={1} alignItems="center" justifyContent="center">
              <Tooltip title="Application lookups" arrow placement="right" sx={tooltipStyle}>
                <a href={ApplicationsRoute} onClick={(e) => handleNavigationClick(e, ApplicationsRoute)} style={{ textDecoration: 'none' }}>
                  <IconButton
                    aria-label="application-lookups"
                    size="large"
                    component="span"
                    disableRipple
                    sx={navIconButtonStyle}
                  >
                    <AppsIcon fontSize="large" sx={navigationIconStyle(isApplicationsActive)} />
                  </IconButton>
                </a>
              </Tooltip>
              <Tooltip title="Organisation lookups" arrow placement="right" sx={tooltipStyle}>
                <a href={OrganisationsRoute} onClick={(e) => handleNavigationClick(e, OrganisationsRoute)} style={{ textDecoration: 'none' }}>
                  <IconButton
                    aria-label="organisation-lookups"
                    size="large"
                    component="span"
                    disableRipple
                    sx={navIconButtonStyle}
                  >
                    <CorporateFareIcon fontSize="large" sx={navigationIconStyle(isOrganisationsActive)} />
                  </IconButton>
                </a>
              </Tooltip>
              <ADSUserAvatar onSaveChanges={handleUserSaveChanges} />
            </Stack>
          </Grid2>
        </Grid2>
      </Drawer>
      <ConfirmEditLossDialog
        isOpen={openConformation}
        title="Unsaved changes"
        message="You have made changes to this record, do you want to keep them or discard them?"
        saveText="Save"
        handleSaveClick={handleSaveChanges}
        handleDisposeClick={handleAllowDispose}
        handleReturnClick={handleCancelMoveAway}
      />
    </>
  );
};

export default ADSNavContent;
